import {
  Component,
  effect,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { DropdownListComponent } from '../../dropdown-list/dropdown-list.component';
import { LoadingAndFeedbackState } from '../../../models/loading-and-feedback-state.type';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconComponent } from '../../icon/icon.component';
import { LoadingSpinnerComponent } from '../../loading-spinner/loading-spinner.component';
import {
  AutoInitialWidthInputFieldDirective,
} from '../../../directives/auto-initial-width-input-field/auto-initial-width-input-field.directive';
import { DropdownOption } from '../../../models/dropdown-option.model';
import { PopoverDirective } from '../../../directives/popover/popover.directive';
import { getMaxLengthFromFormControl } from '../../../functions/get-maxlength-from-form-control';

@Component({
  selector: 'work-ui-input-field-with-options',
  standalone: true,
  templateUrl: './input-field-with-options.component.html',
  styleUrls: ['./input-field-with-options.component.scss'],
  imports: [
    TranslateModule,
    FormsModule,
    LoadingSpinnerComponent,
    IconComponent,
    DropdownListComponent,
    AutoInitialWidthInputFieldDirective,
    PopoverDirective,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputFieldWithOptionsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputFieldWithOptionsComponent),
      multi: true,
    },
  ],
})
export class InputFieldWithOptionsComponent implements OnInit, ControlValueAccessor, Validator, OnDestroy {
  readonly dropdown = viewChild.required('dropdown', { read: DropdownListComponent });
  readonly inputFieldSearch = viewChild.required('inputFieldSearch', { read: ElementRef });
  // General config
  public id = model<string>('');
  public name = input<string>('');
  public label = input<string>('');
  public placeholder = input<string>('');
  public value = model<any>('');
  public valueLabel = model<any>('');
  public disabled = model<boolean>(false);
  public iconName = input<string>('');
  public iconRight = input<boolean>(false);
  public tableInput = input<boolean>(false);
  public editable = input<boolean>(false);
  public maxWidth = input<number | string | undefined>(undefined);
  // Validation
  public required = model<boolean>(false);
  public invalid = signal(false);
  public hintMessage = model<string>('');
  public hideHint = input<boolean>(false);
  public disableHint = input<boolean>(false);
  public maxLength = signal<number | undefined>(undefined);
  public showMaxLengthCounter = signal(false);
  public currentCharLength = signal(0);
  public hasFocus = signal(false);
  public min = input<number | undefined>(undefined);
  public max = input<number | undefined>(undefined);
  public debounceTimeInMs = input<number>(500);
  public formControlName = input<string>('');
  // Loading state
  public showLoadingOnValueChange = input<boolean>(false);
  public loadingState = model<LoadingAndFeedbackState>('none');
  // Dropdown
  public dropdownOptions = input.required<DropdownOption[]>();
  public dynamicDropdownOptions = input<boolean>(false);
  public dropdownShowArrow = input<boolean>(false);
  public readonly = input<boolean>(false);
  public showClearButton = input<boolean>(false);
  public clearInputOnSelect = input<boolean>(false);
  public valueChange = output<any>();
  public dropDownOptionChange = output<DropdownOption>();
  protected dropDownOpen = signal(false);
  private controlContainer = inject(ControlContainer, { optional: true });
  private translateService = inject(TranslateService);
  private formControl: FormControl | undefined;
  private valueChangedSubject = new Subject<any>();
  private inputHintMessage = '';
  private valueChangeSubscriber: Subscription | undefined = undefined;

  private readonly syncValueLabelEffect = effect(() => {
    const value = this.value();
    const selectedValueIsEmpty = value === null || value === undefined || value === '';

    if (selectedValueIsEmpty) {
      this.valueLabel.set('');
      return;
    }

    const matchingOption = this.dropdownOptions().find((dropdownOption) => dropdownOption.value === value);

    if (matchingOption) {
      this.valueLabel.set(matchingOption.label);
    }
  });

  private dropdownOptionsEffect = effect(() => {
    if (this.dynamicDropdownOptions() && this.dropdownOptions().length > 0) {
      this.dropDownOpen.set(true);
    }
  });

  private loadingStateEffect = effect(() => {
    if (this.loadingState() === 'success') {
      this.dropDownOpen.set(true);
    }
  });

  ngOnInit() {
    if (!this.id() && this.name()) {
      this.id.set(this.name());
    }
    this.setOptionsFromFormControl();
    if (this.maxLength() === undefined) {
      this.maxLength.set(10000);
    }
    const initialValue = this.value();
    this.currentCharLength.set(initialValue != null ? String(initialValue).length : 0);

    // save initial hintMessage from parent for validation usage
    this.inputHintMessage = this.hintMessage();

    this.valueChangeSubscriber = this.valueChangedSubject.pipe(debounceTime(this.debounceTimeInMs()), distinctUntilChanged()).subscribe((value) => {
      if (value && (!this.required() || value !== '')) {
        if (this.showLoadingOnValueChange() && value && value.length >= 3) {
          this.loadingState.set('loading');
        }
        this.valueChange.emit(value);
      }
    });
  }

  ngOnDestroy() {
    try {
      this.valueChangedSubject.unsubscribe();
      this.valueChangeSubscriber?.unsubscribe();
    } catch {
      // nothing to catch
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.disabled()) return;

    // Handle different keyboard events based on combobox state
    if (this.dropDownOpen()) {
      // When popover is open
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          event.stopPropagation();
          this.dropDownOpen.set(false);
          this.inputFieldSearch()?.nativeElement.focus();
          break;

        case 'Tab':
          // Allow tab to close and move to next element
          this.dropDownOpen.set(false);
          break;

        case 'ArrowDown':
        case 'ArrowUp':
        case 'Enter':
        case 'Home':
        case 'End':
        case 'PageUp':
        case 'PageDown':
          // Forward navigation to list component
          event.preventDefault();
          this.dropdown()?.onKeyDown(event);
          break;
        case 'ArrowLeft':
        case 'ArrowRight':
          break;
        default:
          // close on typing with dynamic dropdown options
          if (this.dynamicDropdownOptions()) {
            this.dropDownOpen.set(false);
          }
      }
    } else {
      // When popover is closed
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp':
        case 'Enter':
          event.preventDefault();
          if (this.dropdownOptions().length > 0) {
            this.dropDownOpen.set(true);
          }
          break;
        default:
          // open on typing or backspace if there's text
          if ((event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) ||
            (this.valueLabel().length > 0 && event.key === 'Backspace')) {
            this.dropDownOpen.set(!this.dynamicDropdownOptions());
          }
          break;
      }
    }
  }

  protected emitOptionChange(selectedOption: DropdownOption) {
    this.dropDownOptionChange.emit(selectedOption);
    this.value.set(this.clearInputOnSelect() ? '' : selectedOption.value);
    this.valueLabel.set(this.clearInputOnSelect() ? '' : selectedOption.label);

    // Notify form control of the change
    this.onChange(this.value());
  }

  protected onSearchFieldFocus() {
    this.hasFocus.set(true);
    if (!this.dynamicDropdownOptions() && !this.readonly() && !this.disabled()) {
      this.dropDownOpen.set(true);
    }
  }

  protected onSearchFieldBlur() {
    this.hasFocus.set(false);
    this.onTouched();
  }

  /**
   * Clear the input.
   */
  protected clear() {
    this.value.set('');
    this.valueLabel.set('');
    this.onChange(this.value());
    this.validateNow();
    this.inputFieldSearch()?.nativeElement.focus();
    this.dropDownOpen.set(true);
    this.valueChangedSubject.next(this.value());
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: any = () => {
  };
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: any = () => {
  };

  updateValue(newValue: any) {
    if (newValue && newValue.target) {
      newValue = newValue.target.value;
    }
    this.currentCharLength.set(newValue?.length ?? 0);

    // prevent writing more characters if maxLength is set
    const maxLength = this.maxLength();
    if (maxLength !== undefined && newValue.length > maxLength) {
      newValue = newValue.substring(0, maxLength);
      // Adjust currentLength if maxLength is exceeded
      this.currentCharLength.set(maxLength);
    }

    if (newValue === '') {
      newValue = null;
    }

    this.value.set(newValue);
    this.onChange(this.value());
    this.onTouched();
    this.validateNow();
    this.valueChangedSubject.next(this.value());
  }

  writeValue(value: any): void {
    this.value.set(value);
    this.currentCharLength.set(value != null ? String(value).length : 0);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  /**
   * Used for real-time validation while user is typing rather
   * than only triggering when focus is lost on input
   */
  validateNow() {
    if (this.controlContainer && this.formControlName()) {
      this.formControl = this.controlContainer.control?.get(this.formControlName()) as FormControl;
      if (this.formControl) {
        this.validateInput(this.formControl);
      }
    } else {
      const control = new FormControl(this.value());
      control.markAsTouched();
      this.validateInput(control);
    }
  }

  validateInput(control: FormControl) {
    const value = control.value;

    // Reset invalid state at the beginning of validation
    this.invalid.set(false);

    if (control.touched) {
      // Check if the input is required and is empty
      if (value === null || value === undefined || value === '') {
        if (this.required()) {
          this.invalid.set(true);
          this.hintMessage.set('FELD_DARF_NICHT_LEER_SEIN');
          return { required: true };
        }
        return null; // If not required, and value is empty, it's valid
      }

      // Min/Max validation
      const min = this.min();
      if (min !== undefined && value < min) {
        this.invalid.set(true);
        this.hintMessage.set(
          this.inputHintMessage ? this.inputHintMessage : this.translateService.instant('WERT_MUSS_MINDESTENS_X_SEIN', { min }),
        );
        return { min: { min, actual: value } };
      }
      const max = this.max();
      if (max !== undefined && value > max) {
        this.invalid.set(true);
        this.hintMessage.set(
          this.inputHintMessage ? this.inputHintMessage : this.translateService.instant('WERT_DARF_MAXIMAL_X_SEIN', { max }),
        );
        return { max: { max, actual: value } };
      }

      // take native max Length from FormControl
      if (control.errors && Object.prototype.hasOwnProperty.call(control.errors, 'maxlength')) {
        // fallback: if upfront extraction missed it, capture from the error
        const error = control.errors['maxlength'];
        this.maxLength.set(error['requiredLength']);
        this.showMaxLengthCounter.set(true);
      }

      // Maxlength validation
      if (this.maxLength() !== undefined && value.length > <number>this.maxLength()) {
        this.invalid.set(true);
        this.hintMessage.set(this.translateService.instant('MAXIMALE_LAENGE_X_ZEICHEN_ERREICHT', { maxLength: this.maxLength() }));
        return { maxlength: { requiredLength: this.maxLength(), actualLength: value.length } };
      }
    }

    // If the value passes all validations
    return null;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return this.validateInput(control as FormControl);
  }

  private setOptionsFromFormControl() {
    if (this.controlContainer && this.formControlName()) {
      this.formControl = this.controlContainer.control?.get(this.formControlName()) as FormControl;
      if (this.formControl) {
        if (this.formControl.validator) {
          const validator = this.formControl.validator({} as AbstractControl);
          this.required.set(!!(validator && validator['required']));
        }
        const maxLengthFromValidator = getMaxLengthFromFormControl(this.formControl);
        if (maxLengthFromValidator !== undefined) {
          this.maxLength.set(maxLengthFromValidator);
          this.showMaxLengthCounter.set(true);
        }
      }
    }
  }
}
